import React from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Skeleton,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';

function AINewsSkeleton() {
  const theme = useTheme();

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`,
        pb: 6
      }}
    >
      {/* Header Section */}
      <Container maxWidth="xl" sx={{ pt: 3, pb: 2 }}>
        <Box>
          {/* Title and Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Skeleton variant="text" width={320} height={48} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={480} height={28} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Skeleton variant="rounded" width={120} height={42} />
              <Skeleton variant="rounded" width={160} height={42} />
            </Box>
          </Box>

          {/* Status Bar */}
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ 
              mt: 2,
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box display="flex" gap={2} alignItems="center">
              <Skeleton variant="text" width={180} height={20} />
              <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 4 }} />
            </Box>
            <Skeleton variant="rounded" width={120} height={22} sx={{ borderRadius: 4 }} />
          </Box>
        </Box>
      </Container>

      {/* Main Content Container */}
      <Container maxWidth="xl">
        {/* Market Assessment Card */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={240} height={32} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={360} height={24} />
              </Box>
            </Box>

            {/* Metrics Grid */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2.5,
              mb: 3
            }}>
              {[1, 2, 3, 4].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              ))}
            </Box>

            {/* Summary Text */}
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
              <Skeleton variant="text" width="100%" height={24} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="95%" height={24} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="85%" height={24} />
            </Box>
          </CardContent>
        </Card>

        {/* Divider with Label */}
        <Box sx={{ position: 'relative', my: 5 }}>
          <Divider sx={{ borderColor: theme.palette.divider }} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: theme.palette.background.default,
              px: 3,
              py: 0.5
            }}
          >
            <Skeleton variant="text" width={180} height={20} />
          </Box>
        </Box>

        {/* Shock Impact Card */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={220} height={32} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={340} height={24} />
              </Box>
            </Box>

            {/* Shock Metrics */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2.5
            }}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.warning.main, 0.03),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={36} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Enhanced Market Prediction */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={260} height={32} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={380} height={24} />
              </Box>
            </Box>

            {/* Chart Area */}
            <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2, mb: 3 }} />

            {/* Prediction Cards */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.03),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="70%" height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Weekly Forecast */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={240} height={32} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width={320} height={24} />
              </Box>
            </Box>

            {/* Forecast Items */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 4 }} />
                  </Box>
                  <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="75%" height={20} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Indicator Impact Section */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Skeleton variant="text" width={280} height={32} />
            </Box>

            {/* Impact Grid */}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="50%" height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Recent Articles */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={48} height={48} />
                <Skeleton variant="text" width={220} height={32} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="rounded" width={120} height={36} />
                <Skeleton variant="rounded" width={100} height={36} />
              </Box>
            </Box>

            {/* Article List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Skeleton variant="text" width="75%" height={28} />
                    <Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: 4 }} />
                  </Box>
                  <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Skeleton variant="rounded" width={80} height={20} sx={{ borderRadius: 4 }} />
                    <Skeleton variant="rounded" width={100} height={20} sx={{ borderRadius: 4 }} />
                    <Skeleton variant="rounded" width={90} height={20} sx={{ borderRadius: 4 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Animated shimmer effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.5), transparent)',
          animation: 'shimmer 2s infinite',
          zIndex: 9999,
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      />
    </Box>
  );
}

export default AINewsSkeleton;

